import React, {
	Dispatch,
	ReactNode,
	SetStateAction,
	useCallback,
	useEffect,
	useState,
} from 'react';

import { Lyrics as LyricsEditor } from '@/components/Layout/components/AddCustomSong/Lyrics';
import { SongTags } from '@/components/Layout/components/AddCustomSong/SongTags';
import {
	ENERGY_TAGS,
	GOSPEL_TAGS,
	SONG_TYPE_TAGS,
} from '@/config/constants';
import { BottomCard } from '@/components/shared/BottomCard';
import { Chip } from '@/components/shared/Chip';
import {
	AsaphDialogHeader,
	AsaphDialogHeaderButton,
} from '@/components/shared/Dialog';
import AsaphDialog from '@/components/shared/Dialog/AsaphDialog';
import { getRotationStatusClassName } from '@/components/shared/RotationStatusLabel';
import { PANEL_TRANSITION_TIMEOUT, ROTATION_STATUSES } from '@/config/constants';
import {
	Song,
	TeamSong,
	TeamSongFeedback,
	TeamSongFeedbackReport,
	useGetSongMutation,
	useGetTeamSongMutation,
	useUpdateTeamSongMutation,
} from '@/graphql';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useTeamSongs } from '@/providers/TeamSongsProvider';
import { CircularProgress, Fade, Slide } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { pathOr } from 'ramda';

import { AppleMusicUrl } from './AppleMusicUrl';
import { TagPicker } from './TagPicker/TagPicker';
import { DefaultKey } from './DefaultKey';
import { NewSong } from './NewSong';
import { RecordedKey } from './RecordedKey';
import { RotationStatus } from './RotationStatus';
import { SongInformation } from './SongInformation';
import styles from './SongModalPanel.module.scss';
import { SongModalRoot } from './SongModalRoot';
import { SongSettings } from './SongSettings';
import { SpotifyUrl } from './SpotifyUrl';
import { YouTubeUrl } from './YouTubeUrl';

import { ESongModalDisplayState } from './SongModalDisplayState';
export { ESongModalDisplayState };

export type LimitedTeamSongFeedbackReport = Pick<
	TeamSongFeedbackReport,
	| 'startAt'
	| 'meanPersonalScore'
	| 'meanTeamScore'
	| 'meanCongregationalScore'
	| 'setlistId'
>;

type LimitedTeamSongFeedback = Pick<
	TeamSongFeedback,
	| 'personalScoreQuartiles'
	| 'teamScoreQuartiles'
	| 'congregationalScoreQuartiles'
	| 'trend'
> & {
	teamSongFeedbackReports: LimitedTeamSongFeedbackReport[];
};

export type SongModalSong = Pick<
	Song,
	| 'id'
	| 'name'
	| 'artist'
	| 'ccli'
	| 'recordedKey'
	| 'recommendedKey'
	| 'artworkUrl'
	| 'appleMusicId'
	| 'youtubeId'
	| 'spotifyId'
	| 'ccli'
	| 'emotionTags'
	| 'energyTags'
	| 'gospelTags'
	| 'songTypeTags'
	| 'lyrics'
	| 'analysisOutput'
	| 'analysisStatus'
> &
	Pick<TeamSong, 'defaultKey' | 'rotationStatus' | 'originSongId'> & {
		feedback?: LimitedTeamSongFeedback | null;
		new?: TeamSong['new'];
	};

export interface SongModalPanelProps {
	setOpen: (open: boolean) => void;
	config: {
		teamSongId?: string;
		songId?: string;
		viewOnly?: boolean;
	};
}

export interface IPropsSongModalDisplay {
	pushDisplayState: (displayState: ESongModalDisplayState) => void;
	popDisplayState: () => void;
	panelTransitionTimeout: number;
	openedSong: SongModalSong;
	setOpen: SongModalPanelProps['setOpen'];
	setOpenedSong: Dispatch<SetStateAction<SongModalSong | undefined>>;
	updateTeamSong: (teamSongInput: Partial<TeamSong>) => Promise<void>;
	handleClose: () => void;
	titleVisible: boolean | undefined;
	setTitleVisible: Dispatch<SetStateAction<boolean | undefined>>;
	isTeamSong: boolean;
	onSettingsClick?: () => void;
}

const displayStateComponents: Map<
	ESongModalDisplayState,
	{
		title?: string | string[];
		component: React.FC<IPropsSongModalDisplay>;
		defaultTitleVisibility?: boolean;
	}
> = new Map([
	[
		ESongModalDisplayState.ROOT,
		{
			component: SongModalRoot,
			title: ['name'],
			defaultTitleVisibility: false,
		},
	],
	[
		ESongModalDisplayState.SONG_SETTINGS,
		{
			component: SongSettings,
			title: 'Song settings',
		},
	],
	[
		ESongModalDisplayState.SONG_INFORMATION,
		{
			component: SongInformation,
			title: 'Edit song',
		},
	],
	[
		ESongModalDisplayState.SPOTIFY_URL,
		{
			title: 'Spotify URL',
			component: SpotifyUrl,
		},
	],
	[
		ESongModalDisplayState.YOUTUBE_URL,
		{
			title: 'YouTube URL',
			component: YouTubeUrl,
		},
	],
	[
		ESongModalDisplayState.RECORDED_KEY,
		{
			title: 'Recorded key',
			component: RecordedKey,
		},
	],
	[
		ESongModalDisplayState.APPLE_MUSIC_URL,
		{
			title: 'Apple Music URL',
			component: AppleMusicUrl,
		},
	],
	[
		ESongModalDisplayState.LYRICS,
		{
			title: 'Song Lyrics',
			component: ({ openedSong }) => {
				const { lyrics } = openedSong;
				return (
					<div className={styles['lyrics-view']}>
						{lyrics ? (
							<p>{lyrics}</p>
						) : (
							<p className={styles['lyrics-view__empty']}>
								No lyrics available
							</p>
						)}
					</div>
				);
			},
		},
	],
	[
		ESongModalDisplayState.EDIT_LYRICS,
		{
			title: 'Song Lyrics',
			component: ({ openedSong, updateTeamSong, popDisplayState }) => (
				<LyricsEditor
					teamSong={openedSong}
					onSubmit={(values) => {
						updateTeamSong(values);
						popDisplayState();
					}}
				/>
			),
		},
	],
	[
		ESongModalDisplayState.ENERGY_TAG,
		{
			title: 'Energy',
			component: ({ openedSong, updateTeamSong, popDisplayState }) => (
				<TagPicker
					label="Select the energy for"
					songName={openedSong.name}
					items={ENERGY_TAGS}
					selected={openedSong.energyTags ?? []}
					onSave={(tags) => {
						updateTeamSong({ energyTags: tags });
						popDisplayState();
					}}
				/>
			),
		},
	],
	[
		ESongModalDisplayState.SONG_TYPE_TAG,
		{
			title: 'Song Type',
			component: ({ openedSong, updateTeamSong, popDisplayState }) => (
				<TagPicker
					label="Select the song type for"
					songName={openedSong.name}
					items={SONG_TYPE_TAGS}
					selected={openedSong.songTypeTags ?? []}
					onSave={(tags) => {
						updateTeamSong({ songTypeTags: tags });
						popDisplayState();
					}}
				/>
			),
		},
	],
	[
		ESongModalDisplayState.GOSPEL_TAG,
		{
			title: 'Gospel Story',
			component: ({ openedSong, updateTeamSong, popDisplayState }) => (
				<TagPicker
					label="Select the Gospel Story for"
					songName={openedSong.name}
					items={GOSPEL_TAGS}
					selected={openedSong.gospelTags ?? []}
					multiSelect
					onSave={(tags) => {
						updateTeamSong({ gospelTags: tags });
						popDisplayState();
					}}
				/>
			),
		},
	],
	[
		ESongModalDisplayState.SONG_TAGS,
		{
			title: 'Song Tags',
			component: ({ openedSong, updateTeamSong, popDisplayState }) => {
				// eslint-disable-next-line react-hooks/rules-of-hooks
				const fireChangeTagsEvent = useAnalyticsEvent(
					'View_Song_Change_Song_Themes',
				);
				// eslint-disable-next-line react-hooks/rules-of-hooks
				const fireSaveTagsEvent = useAnalyticsEvent(
					'View_Song_Save_Song_Themes',
				);
				return (
					<SongTags
						teamSong={openedSong}
						onChange={(tags) => {
							fireChangeTagsEvent({
								tagsJson: JSON.stringify(tags),
							});
						}}
						onSubmit={(tags) => {
							fireSaveTagsEvent();
							updateTeamSong({ ...tags });
							popDisplayState();
						}}
					/>
				);
			},
		},
	],
	[
		ESongModalDisplayState.ROTATION_STATUS,
		{
			title: 'Rotation Status',
			component: RotationStatus,
		},
	],
	[
		ESongModalDisplayState.DEFAULT_KEY,
		{
			title: 'Default key',
			component: DefaultKey,
		},
	],
	[
		ESongModalDisplayState.NEW_SONG,
		{
			title: 'New song',
			component: NewSong,
		},
	],
]);
const displayStateKeys = Array.from(displayStateComponents.keys());
const defaultDisplayStateStack = [ESongModalDisplayState.ROOT];
const defaultTitleVisible = undefined;

export const SongModalPanel: React.FC<SongModalPanelProps> = ({
	setOpen,
	config,
}) => {
	const [openedSong, setOpenedSong] = useState<SongModalSong | undefined>();
	const { enqueueSnackbar } = useSnackbar();
	const { handleError } = useErrorHandler();
	const { refetch } = useTeamSongs();
	const { mutateAsync } = useUpdateTeamSongMutation();
	const { mutateAsync: getTeamSong, isLoading: isTeamSongLoading } =
		useGetTeamSongMutation();
	const { mutateAsync: getSong, isLoading: isSongLoading } =
		useGetSongMutation();

	useEffect(() => {
		const fetchSong = async () => {
			if (config.teamSongId) {
				try {
					const teamSong = await getTeamSong({
						teamSongId: config.teamSongId,
					});
					setOpenedSong(teamSong.getTeamSong);
				} catch (err) {
					handleError({
						errorCode: 'GetTeamSongError',
						friendlyMessage: 'Failed to load song',
						error: err,
					});
				}
			}
			if (config.songId) {
				try {
					const song = await getSong({ songId: config.songId });
					setOpenedSong(song.getSong);
				} catch (err) {
					handleError({
						errorCode: 'GetSongError',
						friendlyMessage: 'Failed to load song',
						error: err,
					});
				}
			}
		};
		if (!openedSong && !isTeamSongLoading && !isSongLoading) {
			fetchSong();
		}
	}, [
		config.songId,
		config.teamSongId,
		handleError,
		getSong,
		getTeamSong,
		openedSong,
		isTeamSongLoading,
		isSongLoading,
	]);

	const [displayStateStack, setDisplayStateStack] = useState(
		defaultDisplayStateStack,
	);
	const [displayState, setDisplayState] = useState<ESongModalDisplayState>(
		getDisplayState(displayStateStack),
	);
	const [titleVisible, setTitleVisible] = useState<boolean | undefined>(
		defaultTitleVisible,
	);
	const fireErrorEvent = useAnalyticsEvent('Error');
	const fireViewSongSettingsEvent = useAnalyticsEvent(
		'View_Song_View_Settings',
	);

	const { title: panelTitle, defaultVisibility } = getNameForDisplayState(
		displayState,
		openedSong,
	);

	const isTeamSong = !!config.teamSongId;

	useEffect(() => {
		setDisplayState(getDisplayState(displayStateStack));
	}, [displayStateStack]);

	const popDisplayState = useCallback(() => {
		if (displayStateStack.length > 1) {
			const newStack = [...displayStateStack];
			newStack.pop();
			setDisplayStateStack(newStack);
			setTitleVisible(defaultTitleVisible);
		}
	}, [displayStateStack]);

	const pushDisplayState = useCallback(
		(newState: ESongModalDisplayState) => {
			setDisplayStateStack([...displayStateStack, newState]);
			setTitleVisible(defaultTitleVisible);
		},
		[displayStateStack],
	);

	const handleSongSettingsClick = useCallback(() => {
		pushDisplayState(ESongModalDisplayState.SONG_SETTINGS);
		fireViewSongSettingsEvent();
	}, [pushDisplayState, fireViewSongSettingsEvent]);

	const handleClose = useCallback(() => {
		setOpen(false);
		setDisplayStateStack(defaultDisplayStateStack);
		setTitleVisible(defaultTitleVisible);
	}, [setOpen, setDisplayStateStack]);

	const updateTeamSong = useCallback(
		async (teamSongInput: Partial<TeamSong>) => {
			if (!openedSong) {
				return;
			}
			try {
				setOpenedSong((state) => {
					if (state) {
						return { ...state, ...teamSongInput };
					}
				});

				const res = await mutateAsync({
					teamSongId: openedSong.id,
					input: {
						artist: openedSong.artist,
						name: openedSong.name,
						...teamSongInput,
					},
				});
				// Invalidate list of songs in Songs page and in Songlist modal
				refetch();
				setOpenedSong((state) => ({ ...state, ...res.updateTeamSong }));
			} catch (err) {
				enqueueSnackbar(
					'Something went wrong while updating your song',
					{
						variant: 'warning',
					},
				);
				setOpenedSong(openedSong);
				fireErrorEvent({
					name: 'UpdateTeamSongError',
					details: err,
				});
			}
		},
		[
			openedSong,
			enqueueSnackbar,
			mutateAsync,
			setOpenedSong,
			refetch,
			fireErrorEvent,
		],
	);

	if (!openedSong) {
		if (isSongLoading || isTeamSongLoading) {
			return (
				<BottomCard onClose={handleClose}>
					<div className={styles['bottom-card-content']}>
						<CircularProgress />
					</div>
				</BottomCard>
			);
		}
		return null;
	}

	return (
		<AsaphDialog
			open={true}
			onClose={handleClose}
			// paperClassName={styles['song-modal-panel']}
		>
			<div className={styles['song-modal-panel__toolbar']}>
				<AsaphDialogHeader
					leadingButton={{
						needsFadeIn: true,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						component: renderHeaderStart(),
					}}
					title={{
						needsFadeIn: !!(titleVisible ?? defaultVisibility),
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						label: panelTitle,
					}}
					trailingButton={{
						needsFadeIn:
							displayState === ESongModalDisplayState.ROOT &&
							!!openedSong.rotationStatus,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						component: renderHeaderEnd(),
					}}
				/>
			</div>
			{displayState !== ESongModalDisplayState.ROOT && (
				<div className={styles['song-modal-panel__toolbar-divider']}>
					<hr />
				</div>
			)}
			<section className={styles['song-modal-panel__content']}>
				{displayStateKeys.map((key) => {
					const displayStateComponent =
						displayStateComponents.get(key);
					if (!displayStateComponent) {
						return null;
					}
					const Component = displayStateComponent.component;
					if (key === ESongModalDisplayState.ROOT) {
						return (
							<Fade
								key={key}
								in={displayState === key}
								timeout={PANEL_TRANSITION_TIMEOUT}
								mountOnEnter
								unmountOnExit
							>
								<div
									className={
										styles[
											'song-modal-panel__content-container'
										]
									}
								>
									<Component
										pushDisplayState={pushDisplayState}
										popDisplayState={popDisplayState}
										panelTransitionTimeout={
											PANEL_TRANSITION_TIMEOUT
										}
										openedSong={openedSong}
										titleVisible={titleVisible}
										setOpen={setOpen}
										setOpenedSong={setOpenedSong}
										updateTeamSong={updateTeamSong}
										handleClose={handleClose}
										setTitleVisible={setTitleVisible}
										isTeamSong={isTeamSong}
										onSettingsClick={handleSongSettingsClick}
									/>
								</div>
							</Fade>
						);
					}

					return (
						<Slide
							key={key}
							direction="left"
							in={
								displayState === key ||
								displayStateStack.indexOf(key) >= 0
							}
							timeout={PANEL_TRANSITION_TIMEOUT}
							mountOnEnter
							unmountOnExit
						>
							<div
								className={
									styles[
										'song-modal-panel__content-container'
									]
								}
								// Needed to prevent underlying Components' absolute positioned elements be on top of new Component
								style={{
									zIndex: displayStateStack.length,
								}}
							>
								<Component
									pushDisplayState={pushDisplayState}
									popDisplayState={popDisplayState}
									panelTransitionTimeout={
										PANEL_TRANSITION_TIMEOUT
									}
									openedSong={openedSong}
									titleVisible={titleVisible}
									setOpen={setOpen}
									setOpenedSong={setOpenedSong}
									updateTeamSong={updateTeamSong}
									handleClose={handleClose}
									setTitleVisible={setTitleVisible}
									isTeamSong={isTeamSong}
								/>
							</div>
						</Slide>
					);
				})}
			</section>
		</AsaphDialog>
	);

	function renderHeaderStart(): ReactNode {
		switch (displayState) {
			case ESongModalDisplayState.ROOT:
				return (
					<AsaphDialogHeaderButton
						type="text"
						label="Done"
						handlePress={handleClose}
					/>
				);
			case ESongModalDisplayState.SONG_INFORMATION:
				return (
					<AsaphDialogHeaderButton
						type="text"
						label="Cancel"
						handlePress={popDisplayState}
					/>
				);
			default:
				return (
					<AsaphDialogHeaderButton
						type="text"
						label="Back"
						handlePress={popDisplayState}
					/>
				);
		}
	}

	function renderHeaderEnd(): ReactNode {
		if (displayState !== ESongModalDisplayState.ROOT) return null;
		const rotationStatus = openedSong?.rotationStatus;
		if (!rotationStatus) return null;
		const statusEntry = ROTATION_STATUSES.find((s) => s.key === rotationStatus);
		return (
			<Chip
				variant="rounded"
				className={getRotationStatusClassName(rotationStatus)}
			>
				{statusEntry?.capitalisedLabel ?? rotationStatus}
			</Chip>
		);
	}
};

function getDisplayState(displayStateStack: ESongModalDisplayState[]) {
	return displayStateStack.length
		? displayStateStack[displayStateStack.length - 1]
		: ESongModalDisplayState.ROOT;
}

function getNameForDisplayState(
	displayState: ESongModalDisplayState,
	openedSong?: SongModalSong,
): { title: string; defaultVisibility: boolean } {
	const displayStateComponent = displayStateComponents.get(displayState);
	if (displayStateComponent) {
		let title = pathOr('', ['title'], displayStateComponent);
		if (Array.isArray(displayStateComponent.title)) {
			title = pathOr('', displayStateComponent.title, openedSong);
		}
		return {
			title,
			defaultVisibility:
				displayStateComponent.defaultTitleVisibility ?? true,
		};
	}
	return {
		title: '',
		defaultVisibility: true,
	};
}
